import type { LegalDoc } from './legalContentTypes';

export const termsOfServiceEn: LegalDoc = {
  title: 'Terms of Service for Portcierge',
  lastUpdated: 'January 16, 2026',
  sections: [
    {
      heading: 'Agreement to Terms',
      blocks: [
        {
          type: 'p',
          text:
            'By downloading, installing, or using the Portcierge mobile application ("App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.',
        },
      ],
    },
    {
      heading: 'Description of Service',
      blocks: [
        { type: 'p', text: 'Portcierge is a trip planning application that may allow you to:' },
        {
          type: 'ul',
          items: [
            { text: 'Create, edit, and delete trips' },
            { text: 'Add multiple city visits with arrival/departure dates (including cruise port details when relevant)' },
            { text: 'Plan day-by-day itineraries with scheduled items, notes, and lodging' },
            { text: 'Maintain per-city “Options” lists (ideas/checklists)' },
            { text: 'View weather forecasts and inferred weather alerts for planned destinations' },
            { text: 'Generate and share a PDF itinerary' },
            { text: 'Use optional AI tools to generate city plans and extract structured trip info from items you choose to process' },
            { text: 'Enable optional community features such as connections/invites and (if available) location sharing' },
          ],
        },
      ],
    },
    {
      heading: 'User Accounts and Data',
      blocks: [
        {
          type: 'ul',
          items: [
            {
              label: 'No account required for core planning',
              text:
                'Core trip planning features are designed to work without requiring an account, and trip data is stored locally on your device by default.',
            },
            {
              label: 'Optional accounts for community features',
              text:
                'If you enable community features, you may need to authenticate, and related community data may be processed via cloud services.',
            },
            {
              label: 'Data ownership',
              text: 'You retain ownership of the trip data you create in the App.',
            },
          ],
        },
      ],
    },
    {
      heading: 'Acceptable Use',
      blocks: [
        { type: 'p', text: 'You agree to use the App only for lawful purposes. You agree not to:' },
        {
          type: 'ul',
          items: [
            { text: 'Use the App for any illegal or unauthorized purpose' },
            { text: 'Attempt to reverse engineer, interfere with, or modify the App' },
            { text: 'Use the App to harm others or their property' },
          ],
        },
      ],
    },
    {
      heading: 'Weather Data and Alerts',
      blocks: [
        {
          type: 'ul',
          items: [
            { label: 'Third-party weather data', text: 'Weather information may be provided by Open-Meteo or other providers.' },
            { label: 'Planning tool only', text: 'Forecasts and alerts are for planning purposes and may not be accurate or complete.' },
            { label: 'Not emergency alerts', text: 'Weather alerts in the App are not emergency notifications.' },
            { label: 'Use official sources', text: 'Always verify conditions through official sources for critical decisions.' },
          ],
        },
      ],
    },
    {
      heading: 'AI Features (Optional)',
      blocks: [
        {
          type: 'ul',
          items: [
            { label: 'Opt-in', text: 'AI features are optional and may require network connectivity.' },
            {
              label: 'Review before saving',
              text: 'AI outputs are suggestions. You are responsible for reviewing and confirming information before saving it into your trip.',
            },
            { label: 'Third-party processing', text: 'AI requests may be processed by third-party providers (for example, OpenAI).' },
          ],
        },
      ],
    },
    {
      heading: 'Share into the App',
      blocks: [
        {
          type: 'p',
          text:
            'If you use the iOS Share Sheet to send content into Portcierge, those items may be stored locally so you can review them and choose what to save into your trips. Extraction quality can vary depending on the content and available text extraction (e.g., for PDFs).',
        },
      ],
    },
    {
      heading: 'Community and Location Sharing (Optional)',
      blocks: [
        {
          type: 'p',
          text:
            'If you enable community features, the App may support connecting with others and (if available) sharing your location. Location sharing is optional and should be enabled only by explicit user action. Your device settings and permissions control access to location services.',
        },
      ],
    },
    {
      heading: 'Intellectual Property',
      blocks: [
        {
          type: 'ul',
          items: [
            { label: 'App ownership', text: 'Portcierge and its code, design, and content are owned by us and protected by applicable laws.' },
            { label: 'Trademarks', text: 'The Portcierge name and logo may be trademarks.' },
            { label: 'Your content', text: 'You retain rights to the data you create in the App.' },
          ],
        },
      ],
    },
    {
      heading: 'Disclaimers and Limitations',
      blocks: [
        {
          type: 'ul',
          items: [
            { label: 'As-is service', text: 'The App is provided "as is" without warranties of any kind.' },
            { label: 'Availability', text: 'We do not guarantee uninterrupted availability or error-free operation.' },
            { label: 'Your responsibility', text: 'You are responsible for your use of the App and decisions based on its outputs.' },
          ],
        },
      ],
    },
    {
      heading: 'Contact Information',
      blocks: [
        {
          type: 'p',
          text:
            'For questions about these Terms of Service, contact us at wanderer.devs@gmail.com.',
        },
      ],
    },
    {
      heading: 'Acknowledgment',
      blocks: [
        { type: 'p', text: 'By using Portcierge, you acknowledge that you have read and understood these Terms and agree to be bound by them.' },
      ],
    },
  ],
  footerNote: 'These Terms of Service are effective as of the date listed above and apply to all users of the Portcierge app.',
};

